import React, { useState, useEffect } from 'react';
import { api } from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ClaimsByStatus {
  status: string;
  count: number;
  label: string;
}

interface ClaimsByLob {
  lob: string;
  count: number;
  label: string;
}

interface MonthlyVolume {
  month: string;
  count: number;
}

interface AvgAgeByCoverholder {
  company_id: string;
  company_name: string;
  avg_age_days: number;
  open_claims: number;
}

interface ReservesByType {
  reserve_type: string;
  label: string;
  outstanding: number;
  paid: number;
}

interface TopCoverholder {
  company_id: string;
  company_name: string;
  open_claims: number;
  total_incurred: number;
}

interface MiDashboard {
  claims_by_status: ClaimsByStatus[];
  claims_by_lob: ClaimsByLob[];
  monthly_volume: MonthlyVolume[];
  avg_age_by_coverholder: AvgAgeByCoverholder[];
  reserves_by_type: ReservesByType[];
  top_coverholders: TopCoverholder[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  O: '#3b82f6',
  C: '#10b981',
  R: '#f59e0b',
};

const LOB_COLORS = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
];

function formatCompact(value: number): string {
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function formatCurrencyCompact(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function formatMonthLabel(month: string): string {
  // month format: "2025-04" → "Apr 25"
  const [year, mon] = month.split('-');
  const date = new Date(Number(year), Number(mon) - 1, 1);
  const abbr = date.toLocaleString('en-US', { month: 'short' });
  return `${abbr} ${String(year).slice(2)}`;
}

function truncateName(name: string, maxLen = 22): string {
  return name.length > maxLen ? name.slice(0, maxLen - 1) + '…' : name;
}

// ─── Custom Tooltip ───────────────────────────────────────────────────────────

interface TooltipPayloadItem {
  name: string;
  value: number;
  color?: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string | number;
  currency?: boolean;
}

function CustomTooltip({ active, payload, label, currency = false }: CustomTooltipProps) {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-sm">
      {label && <p className="font-semibold text-slate-700 mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || '#334155' }}>
          <span className="font-medium">{p.name}: </span>
          {currency ? formatCurrencyCompact(p.value) : p.value.toLocaleString()}
        </p>
      ))}
    </div>
  );
}

// ─── Chart Card ───────────────────────────────────────────────────────────────

interface ChartCardProps {
  title: string;
  children: React.ReactNode;
}

function ChartCard({ title, children }: ChartCardProps) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
      <p className="text-base font-semibold text-slate-700 mb-4">{title}</p>
      {children}
    </div>
  );
}

// ─── Analytics ────────────────────────────────────────────────────────────────

export default function Analytics() {
  const [data, setData] = useState<MiDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const result: MiDashboard = await api.getMiDashboard();
      setData(result);
    } catch (err: any) {
      setError(err?.message || 'Failed to load analytics data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────

  if (error || !data) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-xl border border-red-200 shadow-sm px-6 py-10 flex flex-col items-center gap-4">
            <svg
              className="w-10 h-10 text-red-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            <p className="text-slate-700 font-medium">{error || 'No data available.'}</p>
            <button
              onClick={fetchDashboard}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Derived data ───────────────────────────────────────────────────────────

  const monthlyVolumeFormatted = data.monthly_volume.map((m) => ({
    ...m,
    monthLabel: formatMonthLabel(m.month),
  }));

  const topAvgAge = [...data.avg_age_by_coverholder]
    .sort((a, b) => b.avg_age_days - a.avg_age_days)
    .slice(0, 10)
    .map((row) => ({
      ...row,
      nameShort: truncateName(row.company_name),
    }));

  const topCoverholders = [...data.top_coverholders]
    .sort((a, b) => b.open_claims - a.open_claims)
    .slice(0, 10)
    .map((row) => ({
      ...row,
      nameShort: truncateName(row.company_name),
    }));

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Page title */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Analytics Dashboard</h1>
          <p className="text-sm text-slate-500 mt-0.5">Management Information</p>
        </div>

        {/* ── Charts Grid ─────────────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* 1. Claims by Status — Pie */}
          <ChartCard title="Claims by Status">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.claims_by_status}
                  dataKey="count"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }: any) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={true}
                >
                  {data.claims_by_status.map((entry) => (
                    <Cell
                      key={entry.status}
                      fill={STATUS_COLORS[entry.status] || '#94a3b8'}
                    />
                  ))}
                </Pie>
                <Tooltip
                  content={
                    <CustomTooltip />
                  }
                />
                <Legend
                  formatter={(value) => (
                    <span className="text-sm text-slate-600">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* 2. Claims by Line of Business — Pie */}
          <ChartCard title="Claims by Line of Business">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data.claims_by_lob}
                  dataKey="count"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, percent }: any) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={true}
                >
                  {data.claims_by_lob.map((entry, index) => (
                    <Cell
                      key={entry.lob}
                      fill={LOB_COLORS[index % LOB_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  formatter={(value) => (
                    <span className="text-sm text-slate-600">{value}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* 3. Monthly Claim Volume — Vertical BarChart */}
          <ChartCard title="Monthly Claim Volume">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={monthlyVolumeFormatted}
                margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="monthLabel"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatCompact}
                  width={40}
                />
                <Tooltip
                  content={<CustomTooltip />}
                />
                <Bar dataKey="count" name="Claims" fill="#3b82f6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* 4. Average Claim Age by Coverholder — Horizontal BarChart */}
          <ChartCard title="Average Claim Age by Coverholder (Top 10)">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={topAvgAge}
                layout="vertical"
                margin={{ top: 4, right: 32, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `${v}d`}
                />
                <YAxis
                  type="category"
                  dataKey="nameShort"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  width={110}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const row = payload[0].payload as (typeof topAvgAge)[0];
                    return (
                      <div className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-sm">
                        <p className="font-semibold text-slate-700 mb-1">{row.company_name}</p>
                        <p className="text-slate-600">
                          Avg Age: <span className="font-medium">{row.avg_age_days.toLocaleString()} days</span>
                        </p>
                        <p className="text-slate-600">
                          Open Claims: <span className="font-medium">{row.open_claims.toLocaleString()}</span>
                        </p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="avg_age_days" name="Avg Age (days)" fill="#f59e0b" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* 5. Reserves by Type — Stacked BarChart */}
          <ChartCard title="Reserves by Type">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={data.reserves_by_type}
                margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatCurrencyCompact}
                  width={56}
                />
                <Tooltip
                  content={<CustomTooltip currency />}
                />
                <Legend
                  formatter={(value) => (
                    <span className="text-sm text-slate-600">{value}</span>
                  )}
                />
                <Bar
                  dataKey="outstanding"
                  name="Outstanding"
                  stackId="reserves"
                  fill="#ef4444"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="paid"
                  name="Paid"
                  stackId="reserves"
                  fill="#10b981"
                  radius={[3, 3, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* 6. Top 10 Coverholders — Horizontal BarChart */}
          <ChartCard title="Top 10 Coverholders by Open Claims">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={topCoverholders}
                layout="vertical"
                margin={{ top: 4, right: 32, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatCompact}
                />
                <YAxis
                  type="category"
                  dataKey="nameShort"
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  width={110}
                />
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const row = payload[0].payload as (typeof topCoverholders)[0];
                    return (
                      <div className="bg-white border border-slate-200 rounded-lg shadow-md px-3 py-2 text-sm">
                        <p className="font-semibold text-slate-700 mb-1">{row.company_name}</p>
                        <p className="text-slate-600">
                          Open Claims: <span className="font-medium">{row.open_claims.toLocaleString()}</span>
                        </p>
                        <p className="text-slate-600">
                          Total Incurred:{' '}
                          <span className="font-medium">{formatCurrencyCompact(row.total_incurred)}</span>
                        </p>
                      </div>
                    );
                  }}
                />
                <Bar
                  dataKey="open_claims"
                  name="Open Claims"
                  fill="#3b82f6"
                  radius={[0, 3, 3, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

        </div>
      </div>
    </div>
  );
}
